import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DOZEN_API_URL } from '../config';
import { DOZEN_APP } from '../apps';
import { profilePath, rememberProfileExit } from '../utils/profileNav';
import AppShell from './AppShell';
import {
  DozenFullBadge,
  DozenHallIcon,
  DozenIconFastest,
  DozenTargetHero,
  DozenTargetMedal,
  dozenHallLabel
} from './DozenHallIcons';
import NominationInfo, { DOZEN_NOMINATION_HELP } from './NominationInfo';
import './HallOfFame.css';

function formatWeekLabel(weekStart) {
  if (!weekStart) return '';
  const start = new Date(`${weekStart}T12:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const sameYear = start.getFullYear() === end.getFullYear();
  const fmt = (d, withYear) =>
    d.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {})
    });
  return `${fmt(start, !sameYear)} — ${fmt(end, true)}`;
}

function dozensLabel(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'дюжин';
  if (mod10 === 1 && mod100 !== 11) word = 'дюжина';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'дюжины';
  return `${n} ${word}`;
}

function HallRow({ badge, name, meta, onOpen }) {
  return (
    <button type="button" className="hall-user" onClick={onOpen}>
      {badge}
      <span className="hall-name">{name}</span>
      {meta}
    </button>
  );
}

function HallPodium({ entries, badge, meta, onOpen }) {
  const top = (entries || []).slice(0, 3);
  return (
    <div className="hall-podium">
      {[top[1], top[0], top[2]].map((entry, slot) => {
        const place = [2, 1, 3][slot];
        if (!entry) return <div key={place} className="hall-podium__slot" />;
        return (
          <button
            key={entry.userId || entry.username}
            type="button"
            className={`hall-podium__card hall-podium__card--${place}`}
            onClick={() => onOpen(entry.username)}
          >
            {badge(entry, place)}
            <span className="hall-podium__name">{entry.username}</span>
            <span className="hall-podium__meta">{meta(entry)}</span>
          </button>
        );
      })}
    </div>
  );
}

function DozenHall() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cutover, setCutover] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${DOZEN_API_URL}/hall`)
      .then((r) => {
        if (!cancelled) {
          setWeeks(r.data.weeks || []);
          setCutover(r.data.cutover || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWeeks([]);
          setCutover(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cutoverLabel =
    cutover != null
      ? `${String(cutover.hour).padStart(2, '0')}:${String(cutover.minute).padStart(2, '0')}`
      : '11:00';

  const openProfile = (username) => {
    rememberProfileExit(location.pathname);
    navigate(profilePath(username, 'dozen'));
  };

  return (
    <AppShell app={DOZEN_APP}>
      <div className="hall-content">
        <div className="hall-hero">
          <DozenTargetHero className="hall-hero-goblet" />
          <div className="hall-hero-text">
            <h1 className="hall-page-title">Знаменитости Тира</h1>
            <p className="hall-hero-sub">Победители Недели</p>
          </div>
        </div>

        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : weeks.length === 0 ? (
          <div className="hall-empty">
            Пока нет закрытых недель. Сыграйте неделю — и здесь появятся имена.
          </div>
        ) : (
          weeks.map((week) => (
            <section key={week.weekStart} className="hall-week panel">
              <h2 className="hall-week-title">Неделя {formatWeekLabel(week.weekStart)}</h2>

              <div className="hall-block">
                <div className="hall-block-head">
                  <DozenHallIcon category="podium" className="badge-icon--lg" />
                  <h3>{dozenHallLabel('podium')}</h3>
                  <NominationInfo text={DOZEN_NOMINATION_HELP.podium} />
                </div>
                {week.podium?.length ? (
                  <HallPodium
                    entries={week.podium}
                    onOpen={openProfile}
                    badge={(_, place) => (
                      <DozenTargetMedal place={place} className="hall-podium__medal" />
                    )}
                    meta={(entry) =>
                      entry.meta?.totalScore != null ? `${entry.meta.totalScore} очк.` : ''
                    }
                  />
                ) : (
                  <p className="hall-none">Нет данных</p>
                )}
              </div>

              <div className="hall-block">
                <div className="hall-block-head">
                  <DozenHallIcon category="fastest" className="badge-icon--lg" />
                  <h3>{dozenHallLabel('fastest')}</h3>
                  <NominationInfo text={DOZEN_NOMINATION_HELP.fastest} />
                </div>
                {week.fastest?.length ? (
                  <ul className="hall-list">
                    {week.fastest.map((entry) => (
                      <li key={`f-${entry.userId}`}>
                        <HallRow
                          badge={
                            <DozenIconFastest
                              place={Number(entry.place) || 1}
                              className="hall-medal"
                            />
                          }
                          name={entry.username}
                          onOpen={() => openProfile(entry.username)}
                          meta={
                            entry.meta?.timeSeconds != null ? (
                              <span className="hall-meta">{entry.meta.timeSeconds}с</span>
                            ) : null
                          }
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hall-none">Нет данных</p>
                )}
              </div>

              <div className="hall-block">
                <div className="hall-block-head">
                  <DozenHallIcon category="full" className="badge-icon--lg" />
                  <h3>{dozenHallLabel('full')}</h3>
                  <NominationInfo text={DOZEN_NOMINATION_HELP.full} />
                </div>
                {week.full?.length ? (
                  <ul className="hall-list">
                    {week.full.map((entry) => (
                      <li key={`full-${entry.userId}`}>
                        <HallRow
                          badge={
                            <DozenFullBadge
                              place={Number(entry.place) || 1}
                              className="hall-medal"
                            />
                          }
                          name={entry.username}
                          onOpen={() => openProfile(entry.username)}
                          meta={
                            <span className="hall-meta">
                              {dozensLabel(entry.meta?.fullDozens ?? 0)}
                            </span>
                          }
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hall-none">Полных дюжин на этой неделе ещё нет</p>
                )}
              </div>

              <div className="hall-block">
                <div className="hall-block-head">
                  <DozenHallIcon category="regulars" className="badge-icon--lg" />
                  <h3>{dozenHallLabel('regulars')}</h3>
                  <NominationInfo text={DOZEN_NOMINATION_HELP.regulars} />
                </div>
                {week.regulars?.length ? (
                  <ul className="hall-list hall-list--wrap">
                    {week.regulars.map((entry) => (
                      <li key={`pf-${entry.userId}`}>
                        <button
                          type="button"
                          className="hall-user hall-user--chip"
                          onClick={() => openProfile(entry.username)}
                        >
                          {entry.username}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="hall-none">Никто не сыграл каждый день</p>
                )}
              </div>
            </section>
          ))
        )}
        <p className="hall-intro">
          Итоги недели публикуются в понедельник в {cutoverLabel} (МСК) — вместе с новой
          дюжиной.
        </p>
      </div>
    </AppShell>
  );
}

export default DozenHall;
